import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { LogIn, Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Login() {
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const doLogin = async (em, pw) => {
        setLoading(true);
        try {
            const u = await login(em, pw);
            toast.success(t("auth.login.welcome", { name: u.name }));
            const from = location.state?.from;
            if (from) navigate(from);
            else {
                const home = u.role === "admin" ? "/admin"
                    : u.role === "doctor" ? "/doctor"
                    : u.role === "employer" ? "/employer"
                    : "/dashboard";
                navigate(home);
            }
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message || "Login failed";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const runDemoLogin = (kind) => {
        if (kind === "admin") {
            setEmail("admin@medxai.co.za");
            setPassword("Admin@MedxAI2026");
            doLogin("admin@medxai.co.za", "Admin@MedxAI2026");
        } else {
            setEmail("patient@medxai.co.za");
            setPassword("Patient@2026");
            doLogin("patient@medxai.co.za", "Patient@2026");
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        await doLogin(email, password);
    };

    return (
        <div className="bg-white min-h-screen" data-testid="login-page">
            <Navbar />
            <main className="pt-28 pb-20">
                <section className="max-w-md mx-auto px-6">
                    <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-3 text-center">{t("auth.login.eyebrow")}</div>
                    <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter text-center mb-8">
                        {t("auth.login.titlePre")}<span className="brand-gradient-text">{t("auth.login.titleAccent")}</span>
                    </h1>

                    <div className="card-soft p-7">
                        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                            <div>
                                <label className="text-xs font-mono tracking-[0.18em] text-gray-500">{t("auth.login.email")}</label>
                                <input
                                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                                    className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                    placeholder="you@email.com"
                                    data-testid="login-email"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-mono tracking-[0.18em] text-gray-500">{t("auth.login.password")}</label>
                                <div className="relative mt-1">
                                    <input
                                        type={show ? "text" : "password"} required value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full px-4 py-3 pr-11 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                        placeholder="••••••••"
                                        data-testid="login-password"
                                    />
                                    <button type="button" onClick={() => setShow((v) => !v)} className="absolute inset-y-0 right-3 grid place-items-center text-gray-400 hover:text-gray-700" data-testid="login-toggle-show">
                                        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <button type="submit" disabled={loading} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="login-submit">
                                <LogIn className="w-4 h-4" /> {loading ? t("auth.login.signing") : t("auth.login.submit")}
                            </button>
                            <div className="text-center">
                                <Link to="/forgot-password" className="text-xs text-gray-500 hover:text-[var(--medx-red)]" data-testid="forgot-password-link">
                                    {t("auth.login.forgot")}
                                </Link>
                            </div>
                        </form>

                        <div className="mt-5 text-center text-sm text-gray-500">
                            {t("auth.login.noAccount")} <Link to="/signup" className="text-[var(--medx-red)] font-semibold hover:underline">{t("auth.login.signup")}</Link>
                        </div>
                        <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-center gap-3 text-xs text-gray-500">
                            <Link to="/doctor/login" className="hover:text-[var(--medx-red)] font-medium" data-testid="login-doctor-portal-link">
                                {t("nav.doctorPortal")} →
                            </Link>
                            <span className="hidden sm:inline text-gray-300">·</span>
                            <Link to="/employer/login" className="hover:text-[var(--medx-red)] font-medium" data-testid="login-employer-portal-link">
                                {t("nav.employerPortal")} →
                            </Link>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}
